import type { NextPage } from 'next'
import { GetStaticProps } from 'next'
import { paths } from '@/constants/paths'
import { AboutContextProvider } from '@/contexts/AboutContext'
import { Seo } from '@/layout/Seo'
import { getWebClient } from '@/services/ApolloClient'
import { OUR_IMPACT_ENTRY_ID, getCollection } from '@/services/CmsService/Collection'
import { GET_PROJECT_TILES } from '@/services/ProjectsService'
import { Collection } from '@/types/codegen-contentful'
import { GetGuildMembershipCountQuery, GetProjectTilesQuery } from '@/types/codegen-federation'
import { getGenericStaticPaths } from '@/utils/GetGenericStaticPaths'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import { getStaticPropsErrorHandler, getUrlFromThisFilePath } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { AboutView } from '@/views/AboutView'
import { GET_GUILD_MEMBERSHIP_COUNT } from '@/views/GuildCreateView/queries'

interface AboutProps {
  impact: Collection
  locale: string
  preview: boolean
  tiles: GetProjectTilesQuery
  members: number
}

const Home: NextPage<AboutProps> = ({ impact, locale, tiles, members }) => {
  const { t } = useTranslate('about')
  const url = `${getBasePath(locale)}`
  const title = t('angelStudiosAboutTitle', 'About Angel Studios | Angel Studios')
  const description = t(
    'angelStudiosAboutDescription',
    'Angel Studios is a leading crowd-funded streaming service and film studio whose mission is to create stories that amplify the light. Learn more about us here.',
  )
  const numberOfGuildMembers = Intl.NumberFormat(locale, { notation: 'compact' }).format(members)

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.about.index}
        title={title}
      />
      <AboutContextProvider impact={impact} tiles={tiles} numberOfGuildMembers={numberOfGuildMembers}>
        <AboutView />
      </AboutContextProvider>
    </>
  )
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps: GetStaticProps = async ({ params, preview = false }) =>
  getStaticPropsErrorHandler({ requestUrl: getUrlFromThisFilePath(params) }, async () => {
    const locale = getLocaleFromParams(params)
    const client = getWebClient()

    const impact = await getCollection({ locale, preview }, OUR_IMPACT_ENTRY_ID)
    const { data: tiles } = await client.query<GetProjectTilesQuery>({
      query: GET_PROJECT_TILES,
      errorPolicy: 'all',
    })

    const { data: members } = await client.query<GetGuildMembershipCountQuery>({
      query: GET_GUILD_MEMBERSHIP_COUNT,
      errorPolicy: 'all',
    })

    return {
      props: {
        impact,
        locale,
        preview,
        tiles,
        members: members.guildMembershipCount,
        ...(await loadTranslations(locale, ['common', 'about', 'app-promo', 'account'])),
      },
    }
  })

export default Home
